"use client";

import type { LocalNotificationID } from "@repo/db/branded-types";
import * as Sentry from "@sentry/nextjs";
import { useCallback } from "react";
import { FeltExperienceError } from "../lib/felt-experience-error";
import { useInitialContext } from "../provider/hooks";
import type { DefaultInitialContext } from "../types";
import publishEventToSDK from "./publish-event-to-sdk";
import { sdkSupportsLocalNotifications } from "./sdk-capabilities";

export interface LocalNotificationSchedule {
  id: LocalNotificationID;
  title: string;
  body: string;
  /** Fires this many seconds from now. Mutually exclusive with `triggerAt`. */
  triggerAfterSeconds?: number;
  /** Fires at this instant. Mutually exclusive with `triggerAfterSeconds`. */
  triggerAt?: Date;
  /** felt-route deeplink opened when the notification is tapped. */
  route?: string;
}

/**
 * Wire payload. `triggerAt` is an absolute ISO-8601 instant, which native
 * schedules as an offset from the moment the request arrives so it fires at the
 * same real-world instant across a time zone or DST change. It must not be
 * converted to local date components: components carry no zone and would be
 * re-resolved at fire time, firing early or, across a spring-forward gap, not
 * at all.
 */
export interface LocalNotificationSchedulePayload {
  id: LocalNotificationID;
  title: string;
  body: string;
  triggerAfterSeconds?: number;
  triggerAt?: string;
  route?: string;
}

/**
 * Validates a schedule and returns the wire payload.
 *
 * These are all cases the native side cannot recover from — a non-positive
 * interval throws inside UNTimeIntervalNotificationTrigger, and a past calendar
 * date simply never fires. Rejecting here turns a silent no-show on the device
 * into an error at the call site.
 *
 * Exported so the debug console can build a valid payload and publish it
 * directly, exercising this validation while bypassing the version gate.
 */
export function buildLocalNotificationPayload(
  schedule: LocalNotificationSchedule
): LocalNotificationSchedulePayload {
  const { id, title, body, triggerAfterSeconds, triggerAt, route } = schedule;

  if (!id) {
    throw new FeltExperienceError("Local notification requires a non-empty id");
  }

  const hasInterval = triggerAfterSeconds !== undefined;
  const hasDate = triggerAt !== undefined;

  if (hasInterval === hasDate) {
    throw new FeltExperienceError(
      `Local notification "${id}" needs exactly one of triggerAfterSeconds or triggerAt`
    );
  }

  if (hasInterval && !(Number.isFinite(triggerAfterSeconds) && triggerAfterSeconds > 0)) {
    throw new FeltExperienceError(
      `Local notification "${id}" needs a positive triggerAfterSeconds, got ${triggerAfterSeconds}`
    );
  }

  if (hasDate) {
    const time = triggerAt.getTime();
    if (Number.isNaN(time)) {
      throw new FeltExperienceError(
        `Local notification "${id}" was given an invalid triggerAt date`
      );
    }
    if (time <= Date.now()) {
      throw new FeltExperienceError(
        `Local notification "${id}" has a triggerAt in the past, which would never fire`
      );
    }
  }

  return {
    id,
    title,
    body,
    ...(hasInterval ? { triggerAfterSeconds } : {}),
    ...(hasDate ? { triggerAt: triggerAt.toISOString() } : {}),
    ...(route ? { route } : {}),
  };
}

/**
 * Schedules an on-device notification. No-op when the host SDK is too old.
 *
 * @throws FeltExperienceError when the schedule is malformed.
 */
export function scheduleLocalNotification(
  schedule: LocalNotificationSchedule,
  sdkVersion: string
): void {
  const payload = buildLocalNotificationPayload(schedule);

  if (!sdkSupportsLocalNotifications(sdkVersion)) {
    console.warn(
      `SDK version ${sdkVersion} does not support local notifications`
    );
    return;
  }

  Sentry.addBreadcrumb({
    category: "local-notification",
    message: "Scheduling local notification",
    level: "info",
    data: { id: payload.id, hasRoute: payload.route !== undefined },
  });

  publishEventToSDK({ topic: "scheduleLocalNotification", data: payload });
}

/** Cancels one or more pending notifications by id. No-op on an empty list. */
export function cancelLocalNotification(
  idOrIds: LocalNotificationID | LocalNotificationID[],
  sdkVersion: string
): void {
  const ids = Array.isArray(idOrIds) ? idOrIds : [idOrIds];

  if (ids.length === 0) return;

  if (!sdkSupportsLocalNotifications(sdkVersion)) {
    console.warn(
      `SDK version ${sdkVersion} does not support local notifications`
    );
    return;
  }

  Sentry.addBreadcrumb({
    category: "local-notification",
    message: "Cancelling local notifications",
    level: "info",
    data: { ids },
  });

  publishEventToSDK({ topic: "cancelLocalNotification", data: { ids } });
}

/**
 * Schedule/cancel bound to the host's SDK version.
 *
 * Suspends until the initial context arrives, so callers must sit inside a
 * Suspense boundary. Reading the version optimistically instead would report
 * the host as unsupported on first render and silently drop an early schedule.
 */
export function useLocalNotifications(): {
  schedule: (schedule: LocalNotificationSchedule) => void;
  cancel: (idOrIds: LocalNotificationID | LocalNotificationID[]) => void;
  supported: boolean;
} {
  const initialContext = useInitialContext<DefaultInitialContext>();
  const sdkVersion = initialContext.app?.sdkVersion ?? "0";

  const schedule = useCallback(
    (next: LocalNotificationSchedule) =>
      scheduleLocalNotification(next, sdkVersion),
    [sdkVersion]
  );

  const cancel = useCallback(
    (idOrIds: LocalNotificationID | LocalNotificationID[]) =>
      cancelLocalNotification(idOrIds, sdkVersion),
    [sdkVersion]
  );

  return {
    schedule,
    cancel,
    supported: sdkSupportsLocalNotifications(sdkVersion),
  };
}
